import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, ActivationEnd, Data, Params, Router } from '@angular/router';
import { LoggerService } from '@shared/utils';
import { isEqual, uniqWith } from 'lodash-es';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

export interface Breadcrumb<T = never> {
  label: string;
  routerLink?: string[];
  skipLocationChange?: boolean;
  queryParams?: Params;
  icon?: string;
  state?: {
    id: number;
  };
  command?: (input?: T) => void;
}

/**
TODO: Zvazit nahrazeni jednoduchou implementací z 
https://primeng.org/breadcrumb
*/
@Injectable({
  providedIn: 'root',
})
export class BreadcrumbsService {
  private router = inject(Router);
  private loggerService = inject(LoggerService);
  private logger = this.loggerService.createLogger('SharedBreadcrumbsService');
  private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);
  readonly breadcrumbs$ = this._breadcrumbs$.asObservable().pipe(distinctUntilChanged(isEqual));

  init() {
    this.logger.debug('init breadcrumbs');

    // Zpracování aktuálního stavu routeru po reloadu app (F5)]
    this.processRouterState(this.router.routerState.snapshot.root);

    this.router.events
      .pipe(
        filter((event) => event instanceof ActivationEnd),
        map((event) => (event as ActivationEnd).snapshot.data)
      )
      .subscribe(() => {
        this.logger.debug('subscribe breadcrumbs');
        const root = this.router.routerState.snapshot.root;
        const breadcrumbs: Breadcrumb[] = [];

        this.addBreadcrumb(root, [], breadcrumbs);
        this._breadcrumbs$.next(
          uniqWith(
            breadcrumbs.filter((breadcrumb) => breadcrumb.label && breadcrumb.label?.length > 0),
            isEqual
          )
        );
      });
  }

  private processRouterState(root: ActivatedRouteSnapshot) {
    const breadcrumbs: Breadcrumb[] = [];
    this.addBreadcrumb(root, [], breadcrumbs);
    this._breadcrumbs$.next(
      uniqWith(
        breadcrumbs.filter((breadcrumb) => breadcrumb.label && breadcrumb.label?.length > 0),
        isEqual
      )
    );
  }

  private addBreadcrumb(route: ActivatedRouteSnapshot | null, parentUrl: string[], breadcrumbs: Breadcrumb[]) {
    if (!route) return;
    const routeUrl = parentUrl.concat(route.url.map((url) => url.path));
    const routeData = route.data;
    const removeRootbreadcrumbs = routeData['removeRootbreadcrumbs'] ?? false;
    if (removeRootbreadcrumbs) {
      const rootLabel = breadcrumbs[0]?.label;
      if (rootLabel) {
        // Najít všechny indexy, které mají label roven rootLabel
        let index = 0;
        while (index < breadcrumbs.length) {
          if (breadcrumbs[index].label === rootLabel) {
            breadcrumbs.splice(index, 1);
          } else {
            index++;
          }
        }
      }
    }
    if (routeData['pageTitle'] || routeData['breadcrumbs']) {
      const routerLink = '/' + routeUrl.join('/');
      const breadcrumb = {
        label: this.getLabel(routeData),
        routerLink: routeData['disabled']
          ? undefined // or you can use undefined
          : routeData['modifyBreadcrumbsPath']
            ? routeData['modifyBreadcrumbsPath'](routerLink)
            : [routerLink],
      };
      breadcrumbs.push(breadcrumb);
    }

    // Add another element for the next route part
    this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
  }

  private getLabel(data: Data) {
    // The breadcrumb can be defined as a static string or as a function
    // to construct the breadcrumb element out of the route data
    if (data['breadcrumbs'])
      return typeof data['breadcrumbs'] === 'function' ? data['breadcrumbs'](data) : data['breadcrumbs'];
    else if (data['pageTitle'] && data['breadcrumbs'] !== null)
      return typeof data['pageTitle'] === 'function' ? data['pageTitle'](data) : data['pageTitle'];
    return '';
  }
}
